import { useState, useRef, useEffect } from 'react';
import { useAtom } from 'jotai';
import { Send, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { currentProjectAtom } from '@/stores/editorStore';
import { apiRequest } from '@/lib/api';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  changes?: CodeChange[];
  collapsed?: boolean;
}

interface CodeChange {
  file: string;
  action: string;
  summary: string;
  diff?: string;
}

export default function ChatPane() {
  const [currentProject] = useAtom(currentProjectAtom);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // CRITICAL: Track current project ID in a ref for accurate race condition checks
  // Using a ref ensures we always have the absolute latest project ID, even during rapid switches
  const currentProjectIdRef = useRef<string | null>(null);

  // Update ref immediately whenever project changes
  useEffect(() => {
    currentProjectIdRef.current = currentProject?.id || null;
    console.log(`[ChatPane] Project changed to: ${currentProjectIdRef.current}`);
  }, [currentProject?.id]);


  // Store draft input per project
  const draftInputsRef = useRef<Map<string, string>>(new Map());
  const previousProjectIdRef = useRef<string | null>(null);

  // Save/restore draft input when project changes
  useEffect(() => {
    const newProjectId = currentProject?.id || null;
    const previousProjectId = previousProjectIdRef.current;

    // Save current input for the previous project
    if (previousProjectId && input.trim()) {
      draftInputsRef.current.set(previousProjectId, input);
    }

    // Load saved input for the new project
    if (newProjectId) {
      const savedInput = draftInputsRef.current.get(newProjectId) || '';
      setInput(savedInput);
    } else {
      setInput('');
    }

    // Update the ref
    previousProjectIdRef.current = newProjectId;
  }, [currentProject?.id]);

  // Load chat history when project changes
  useEffect(() => {
    if (currentProject) {
      loadChatHistory();
    } else {
      setMessages([]);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChatHistory = async () => {
    if (!currentProject) return;

    try {
      const response = await apiRequest<any[]>('get', `/projects/${currentProject.id}/chat`);
      const loadedMessages: ChatMessage[] = response.map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at).getTime(),
        changes: msg.changes || undefined,
        collapsed: msg.role === 'assistant' ? true : undefined,
      }));
      setMessages(loadedMessages);
    } catch (error) {
      console.error('Failed to load chat history:', error);
      // Don't show error to user - just start with empty chat
      setMessages([]);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !currentProject || isLoading) return;

    // CRITICAL: Capture the project ID at send time to prevent race conditions
    const projectIdAtSendTime = currentProject.id;
    const messageContent = input.trim();

    // Clear input and draft immediately
    setInput('');
    if (currentProject?.id) {
      draftInputsRef.current.delete(currentProject.id);
    }

    // CRITICAL: Validate we're still on the same project before adding user message
    if (currentProject?.id !== projectIdAtSendTime) {
      console.log(`[ChatPane] User switched projects before message could be added - discarding`);
      return;
    }

    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Call Claude Code API
      const response = await apiRequest<any>('post', `/projects/${projectIdAtSendTime}/chat`, {
        message: messageContent,
        history: messages.map(m => ({ role: m.role, content: m.content })),
      });

      // CRITICAL: Only add response if we're still on the same project
      // This prevents messages from appearing in the wrong project
      if (currentProject?.id === projectIdAtSendTime) {
        const assistantMessage: ChatMessage = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: response.fullOutput || response.summary,
          timestamp: Date.now(),
          changes: response.changes,
          collapsed: true,
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        console.log(`[ChatPane] Discarding response for project ${projectIdAtSendTime} because user switched to project ${currentProject?.id}`);
        // Reload the history for the project we switched back to, in case the response was saved
        loadChatHistory();
      }
    } catch (error: any) {
      // CRITICAL: Only add error message if we're still on the same project
      if (currentProject?.id === projectIdAtSendTime) {
        const errorMessage: ChatMessage = {
          id: `msg-${Date.now()}-error`,
          role: 'assistant',
          content: `Error: ${error.message}`,
          timestamp: Date.now(),
        };
        setMessages(prev => [...prev, errorMessage]);
      } else {
        console.log(`[ChatPane] Discarding error for project ${projectIdAtSendTime} because user switched to project ${currentProject?.id}`);
      }
    } finally {
      // Only clear loading state if we're still on the same project
      if (currentProject?.id === projectIdAtSendTime) {
        setIsLoading(false);
      }
    }
  };

  const toggleCollapse = (messageId: string) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId ? { ...msg, collapsed: !msg.collapsed } : msg
      )
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="border-b px-4 py-3">
        <h2 className="font-semibold">AI Assistant</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Powered by Claude Code
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <p>No messages yet. Ask me anything about your project!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}
              >
                <div className="whitespace-pre-wrap break-words">
                  {message.content}
                </div>

                {/* Code Changes Section */}
                {message.changes && message.changes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <button
                      onClick={() => toggleCollapse(message.id)}
                      className="flex items-center gap-2 text-xs font-medium hover:opacity-80 transition-opacity"
                    >
                      {message.collapsed ? (
                        <ChevronRight className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                      {message.changes.length} file{message.changes.length !== 1 ? 's' : ''} changed
                    </button>

                    {!message.collapsed && (
                      <div className="mt-2 space-y-2">
                        {message.changes.map((change, idx) => (
                          <div key={idx} className="text-xs">
                            <div className="font-mono font-medium">{change.file}</div>
                            <div className="text-muted-foreground">{change.action}: {change.summary}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="text-xs opacity-50 mt-2">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Loading indicator in chat area */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Claude is thinking...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={currentProject ? "Ask Claude about your code..." : "Select a project first..."}
            disabled={!currentProject || isLoading}
            className="flex-1 px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!currentProject || !input.trim() || isLoading}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
